// One place for the plain words we use to describe where a member is:
// still in onboarding (which gate), in formation (which stage), or delivered
// and living in the portal.
import {
  addressSettled,
  intakeDone,
  isOnboardingComplete,
  isSuitePath,
  meaSigned,
  nameSettled,
  type OnboardingRecord,
} from "./onboardingComplete";

/** The same labels the fulfillment board uses. */
export const FULFILLMENT_STAGE_LABEL: Record<string, string> = {
  pending: "Not started",
  ready: "Ready",
  ein_acquired: "EIN in hand",
  filings_submitted: "Filed with the state",
  filings_received: "State has processed",
  docs_sent: "Documents shipped",
  docs_delivered: "Documents delivered",
};

/** Stages that mean the member is in formation, not yet in the portal. */
export const FORMATION_STAGES = [
  "pending",
  "ready",
  "ein_acquired",
  "filings_submitted",
  "filings_received",
  "docs_sent",
];

export type StageRecord = OnboardingRecord & { fulfillment_stage?: string | null };

/** The gate the team is waiting on, in the pipeline's own words. */
export function currentGateLabel(record: OnboardingRecord): string {
  if (!record) return "Getting started";
  if (!intakeDone(record)) return "Filling in their details";
  if (!nameSettled(record)) {
    const status = record.una_name_status;
    if (status === "submitted" || status === "change_requested") return "Waiting on name review";
    if (status === "approved") return "Confirming their record";
    return "Choosing a name";
  }
  if (!meaSigned(record)) return "Waiting on their signature";
  if (!addressSettled(record)) {
    if (isSuitePath(record)) return "Waiting on the lease signature";
    return record.ca_address_option ? "Settling their address" : "Choosing an address";
  }
  return "Ready for handoff";
}

export type MemberStage =
  | { kind: "none"; label: string }
  | { kind: "onboarding"; label: string }
  | { kind: "formation"; label: string }
  | { kind: "portal"; label: string };

/**
 * Where this person is, given their member record (may be missing for older
 * accounts) and their portal journey progress.
 */
export function memberStage(
  record: StageRecord | null | undefined,
  journey: { percent: number; completed: boolean },
): MemberStage {
  const stage = record?.fulfillment_stage ? String(record.fulfillment_stage) : null;
  const delivered = stage === "docs_delivered";

  if (record && !delivered) {
    if (!isOnboardingComplete(record)) {
      return { kind: "onboarding", label: `Onboarding: ${currentGateLabel(record)}` };
    }
    if (stage && FORMATION_STAGES.includes(stage)) {
      return { kind: "formation", label: `Formation: ${FULFILLMENT_STAGE_LABEL[stage] || stage}` };
    }
    return { kind: "formation", label: "Formation: Ready" };
  }

  if (journey.completed) return { kind: "portal", label: "Journey complete" };
  if (journey.percent > 0) return { kind: "portal", label: `In journey (${journey.percent}%)` };
  if (record) return { kind: "portal", label: "Documents delivered" };
  return { kind: "none", label: "Not started" };
}
